import { StyledInput } from 'Components/Inputs/StyledInput'
import styled from 'styled-components'
import { useCallback, useMemo, useRef } from 'react'
import { FlexColumn, GridColumn, spacingMD, spacingSM } from 'Utils/styles/spacing'
import { UserStatus } from 'Utils/types/userStatus'
import { titleize } from 'Utils/helpers/text'
import { CountryCode } from 'Utils/types/countries'

import { PeopleTableFilters } from 'Pages/Employer/PeoplePage/PeopleTable/PeopleTable'
import { SelectOption, StyledSelect, StyledSelectRef } from 'Components/Inputs/StyledSelect'
import { StyledButton } from 'Components/Buttons/StyledButton'
import { ClearFiltersButton } from 'Components/Table/ClearFiltersButton'

import { ModalType, usePeoplePageContext } from 'Contexts/PeoplePageContext'
import { useEmployerInfoContext } from 'Contexts/EmployerInfoContext'
import { useConfirmationModalContext } from 'Hooks/modals/useConfirmationModal'
import { useResendPendingInvitesQuery } from 'Hooks/api/JOONAdmin/useResendPendingInvitesQuery'
import { useProgramOptions } from 'Hooks/useProgramOptions'
import { useCountries } from 'Hooks/useCountries'
import { useDebouncedSearchInput } from 'Hooks/useDebouncedSearchInput'
import { fontSizeSM, fontWeightSlightlyBold, TableLabel } from 'Utils/styles/text'
import { elfGreen, elfGreenActive, elfGreenHover, white } from 'Utils/styles/colors'

import {
  DropdownContent,
  DropdownItem,
  DropdownRoot,
  DropdownTrigger
} from 'Components/DropdownMenu/DropdownMenu'

import { PaperPlaneIcon, Pencil1Icon, TableIcon, UploadIcon } from '@radix-ui/react-icons'
import { FormattedMessage } from 'react-intl'
import { SimplifiedUserPermission } from 'Utils/types/roles'

const StyledDropdownTrigger = styled(DropdownTrigger)`
  height: 48px;
  border-radius: 8px;
  cursor: pointer;
  background-color: ${elfGreen};
  color: ${white};

  &:hover,
  &:focus {
    background-color: ${elfGreenHover};
  }

  &:active {
    background-color: ${elfGreenActive};
  }

  font-family: 'BasisGrotesquePro', sans-serif;
  font-weight: ${fontWeightSlightlyBold};
  font-size: ${fontSizeSM};
  line-height: 1;
`

const ActionsContainer = styled.div<{
  showProgramFilters: boolean
  showCountryFilter: boolean
}>`
  display: grid;
  grid-column-gap: ${spacingMD};
  align-items: center;
  grid-template-columns:
    auto
    ${({ showProgramFilters }) => (showProgramFilters ? ` 25% ` : '')}
    10%
    ${({ showCountryFilter }) => (showCountryFilter ? ` 10% ` : '')}
    10%
    10%
    11%;
`

type PeopleTableFiltersProps = {
  updateFilters: (newFilters: Partial<PeopleTableFilters>) => void
  clearFilters?: () => void
  isJoonAdmin?: boolean
}

export const PeopleTableActions = ({
  updateFilters,
  clearFilters,
  isJoonAdmin = false
}: PeopleTableFiltersProps) => {
  const { setModal } = usePeoplePageContext()
  const {
    state: { employerId }
  } = useEmployerInfoContext()
  const { showModal } = useConfirmationModalContext()
  const resendPendingInvitesQuery = useResendPendingInvitesQuery()
  const { hasMultipleCountries, countryOptionsWithAll } = useCountries()

  const programSelectRef = useRef<StyledSelectRef | null>(null)
  const countrySelectRef = useRef<StyledSelectRef | null>(null)
  const statusSelectRef = useRef<StyledSelectRef | null>(null)
  const permissionSelectRef = useRef<StyledSelectRef | null>(null)

  const resetSelects = useCallback(() => {
    programSelectRef?.current?.resetSelect()
    countrySelectRef?.current?.resetSelect()
    statusSelectRef?.current?.resetSelect()
  }, [programSelectRef, countrySelectRef, statusSelectRef])

  const updateQueryFilter = useCallback(
    (query: string) => updateFilters({ query }),
    [updateFilters]
  )

  const {
    value: searchInputValue,
    setValue: setSearchInputValue,
    flush
  } = useDebouncedSearchInput(updateQueryFilter, 3000)

  const resetFilters = useMemo(() => {
    return searchInputValue || clearFilters
      ? () => {
          resetSelects()
          setSearchInputValue('')
          if (clearFilters) clearFilters()
        }
      : undefined
  }, [clearFilters, resetSelects, searchInputValue, setSearchInputValue])

  const handleStatusChange = useCallback(
    (status: UserStatus | '') => {
      updateFilters({ status: status !== '' ? status : undefined })
    },
    [updateFilters]
  )
  const handlePermissionChange = useCallback(
    (permission: SimplifiedUserPermission | '') => {
      updateFilters({ permission: permission !== '' ? permission : undefined })
    },
    [updateFilters]
  )
  const handleCountryChange = useCallback(
    (country: CountryCode | '') => {
      updateFilters({ country: country !== '' ? country : undefined })
    },
    [updateFilters]
  )
  const handleBenefitProgramChange = useCallback(
    (benefitProgram: string) => updateFilters({ filterProgramName: benefitProgram }),
    [updateFilters]
  )

  const showInviteUserModal = useCallback(() => setModal(ModalType.InviteUser), [setModal])
  const showBulkUserManagementModal = useCallback(
    () => setModal(ModalType.BulkUserManagement),
    [setModal]
  )
  const showBulkMetadataGridModal = useCallback(
    () => setModal(ModalType.BulkMetadataGrid),
    [setModal]
  )
  const showBulkPurchaseUploadModal = useCallback(
    () => setModal(ModalType.BulkPurchaseUpload),
    [setModal]
  )

  const onResendPendingInvites = useCallback(async () => {
    const confirmed = await showModal(
      'Resend Pending Invites',
      'This resends the invite email to everyone at this employer who has not accepted their ' +
        'invite yet. Invites scheduled for a later date are not sent early.',
      'users.resendPendingInvites.confirm',
      'confirmation.cancel.button'
    )
    if (confirmed && employerId) {
      resendPendingInvitesQuery.mutate(employerId)
    }
  }, [employerId, resendPendingInvitesQuery, showModal])

  const userStatusOptions = useMemo<{ value: UserStatus | ''; label: string }[]>(
    () => [
      { value: '', label: 'All Users' },
      ...Object.values(UserStatus)
        .filter(
          (status) => ![UserStatus.ScheduledInvite, UserStatus.ScheduledRemoval].includes(status)
        )
        .map((value) => ({
          value: value,
          label: titleize(value)
        }))
    ],
    []
  )
  const userPermissionsOptions = useMemo<{ value: SimplifiedUserPermission | ''; label: string }[]>(
    () => [
      { value: '', label: 'All Permissions' },
      { value: SimplifiedUserPermission.ADMIN, label: 'Admin' }
    ],
    []
  )

  const { programOptions } = useProgramOptions({ useName: true, forEmployerAdmin: true })
  const bpOptions: SelectOption[] = [{ value: '', label: 'All Programs' }, ...programOptions]

  return (
    <FlexColumn gap={spacingSM}>
      <GridColumn>
        <TableLabel>Users</TableLabel>
        <ClearFiltersButton onClear={resetFilters} />
      </GridColumn>
      <ActionsContainer
        showProgramFilters={programOptions.length > 1}
        showCountryFilter={hasMultipleCountries}
      >
        <StyledInput
          errorPresent={false}
          placeholder="Search by name, email, or metadata"
          value={searchInputValue}
          onChange={(e) => setSearchInputValue(e.target.value)}
          onBlur={flush}
          onKeyUp={({ key }) => key === 'Enter' && flush()}
        />
        {programOptions.length > 1 && (
          <StyledSelect
            ref={programSelectRef}
            handleChange={handleBenefitProgramChange}
            options={bpOptions}
            defaultValue={bpOptions[0]?.value}
          />
        )}
        <StyledSelect
          ref={statusSelectRef}
          handleChange={handleStatusChange}
          options={userStatusOptions}
          defaultValue={userStatusOptions[0]?.value}
        />
        <StyledSelect
          ref={permissionSelectRef}
          handleChange={handlePermissionChange}
          options={userPermissionsOptions}
          defaultValue={userPermissionsOptions[0]?.value}
        />
        {hasMultipleCountries && (
          <StyledSelect
            ref={countrySelectRef}
            handleChange={handleCountryChange}
            options={countryOptionsWithAll}
            defaultValue={countryOptionsWithAll[0].value}
          />
        )}
        <StyledButton primary id="users.invite" onClick={showInviteUserModal} />

        <DropdownRoot>
          <StyledDropdownTrigger>
            <FormattedMessage id="users.bulkActions" />
          </StyledDropdownTrigger>
          <DropdownContent>
            {isJoonAdmin && (
              <DropdownItem onSelect={showBulkUserManagementModal}>
                <Pencil1Icon />
                <FormattedMessage id="users.bulkUserManagement" />
              </DropdownItem>
            )}
            {isJoonAdmin && (
              <DropdownItem onSelect={showBulkPurchaseUploadModal}>
                <UploadIcon />
                <FormattedMessage id="users.bulkPurchaseUpload" />
              </DropdownItem>
            )}
            {isJoonAdmin && (
              <DropdownItem
                onSelect={onResendPendingInvites}
                disabled={resendPendingInvitesQuery.isLoading}
              >
                <PaperPlaneIcon />
                <FormattedMessage id="users.resendPendingInvites" />
              </DropdownItem>
            )}
            <DropdownItem onSelect={showBulkMetadataGridModal}>
              <TableIcon />
              <FormattedMessage id="users.bulkMetadataGrid" />
            </DropdownItem>
          </DropdownContent>
        </DropdownRoot>
      </ActionsContainer>
    </FlexColumn>
  )
}
